import { combineEpics } from 'redux-observable'
import { Observable } from 'rxjs'
import { list, invite, disable } from 'store/services/courier'
import { alertSuccess, alertErrorDefault } from 'store/ducks/alert'
import * as authenticationDuck from 'store/ducks/authentication'
import { intl } from 'i18n'

// Actions
export const FETCH_REQUEST = 'courier/FETCH_REQUEST'
export const FETCH_SUCCESS = 'courier/FETCH_SUCCESS'
export const FETCH_FAILURE = 'courier/FETCH_FAILURE'
export const INVITATION_REQUEST = 'courier/INVITATION_REQUEST'
export const INVITATION_SUCCESS = 'courier/INVITATION_SUCCESS'
export const INVITATION_FAILURE = 'courier/INVITATION_FAILURE'
export const DISABLE_REQUEST = 'courier/DISABLE_REQUEST'
export const DISABLE_SUCCESS = 'courier/DISABLE_SUCCESS'
export const DISABLE_FAILURE = 'courier/DISABLE_FAILURE'
export const OPEN_MODAL = 'courier/OPEN_MODAL'
export const CLOSE_MODAL = 'courier/CLOSE_MODAL'

// Reducer
const INITIAL_STATE = {
  modal: false,
  loading: false,
  result: [],
  entities: {},
  errors: [],
  invitation: {
    loading: false,
    done: false,
    violations: [],
  },
}

export function reducer(state = INITIAL_STATE, action = {}) {
  switch (action.type) {
    case FETCH_REQUEST:
      return {
        ...state,
        loading: true,
        result: action.reset ? [] : state.result,
        entities: action.reset ? {} : state.entities,
        errors: action.reset ? [] : state.errors,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        result: action.result,
        entities: action.entities,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        result: [],
        entities: {},
        errors: action.errors,
      }
    case OPEN_MODAL:
      return {
        ...state,
        modal: true,
      }
    case CLOSE_MODAL:
      return {
        ...state,
        invitation: {
          loading: false,
          done: false,
          violations: [],
        },
        modal: false,
      }
    case INVITATION_REQUEST:
      return {
        ...state,
        invitation: {
          loading: true,
          done: false,
          violations: [],
        },
      }
    case INVITATION_SUCCESS:
      return {
        ...state,
        invitation: {
          loading: false,
          done: true,
          violations: [],
        },
      }
    case INVITATION_FAILURE:
      return {
        ...state,
        invitation: {
          loading: false,
          done: false,
          violations: action.violations,
        },
      }
    default:
      return state
  }
}

// Actions Creators
export const fetchRequest = (company, reset = true) => ({
  type: FETCH_REQUEST,
  company,
  reset,
})

export const fetchSuccess = (payload) => ({
  type: FETCH_SUCCESS,
  result: payload.map((courier) => courier.id),
  entities: payload.reduce((entities, courier) => ({ ...entities, [courier.id]: courier }), {}),
})

export const fetchFailure = (errors = []) => ({
  type: FETCH_FAILURE,
  errors,
})

export const invitationRequest = (payload) => ({
  type: INVITATION_REQUEST,
  payload,
})

export const invitationSuccess = () => ({
  type: INVITATION_SUCCESS,
})

export const invitationFailure = (violations = []) => ({
  type: INVITATION_FAILURE,
  violations,
})

export const disableRequest = (courier) => ({
  type: DISABLE_REQUEST,
  courier,
})

export const disableSuccess = (courier) => ({
  type: DISABLE_SUCCESS,
  courier,
})

export const disableFailure = (courier) => ({
  type: DISABLE_FAILURE,
  courier,
})

export const openModal = () => ({
  type: OPEN_MODAL,
})

export const closeModal = () => ({
  type: CLOSE_MODAL,
})

// Epics
export const epic = combineEpics(
  fetchRequestEpic,
  invitationRequestEpic,
  invitationSuccessEpic,
  disableRequestEpic,
  disableSuccessEpic,
  sendAlertOnfailureEpic,
  sendAlertOnDisableSuccessEpic,
  sendAlertOnInvitationSuccessEpic,
)

export function fetchRequestEpic(action$) {
  return action$.ofType(FETCH_REQUEST).mergeMap((action) =>
    Observable.ajax(list({ params: { id: action.company.id } }))
      .map((res) => fetchSuccess(res.response.couriers))
      .catch((err) => {
        if (typeof err === 'object' && (err.response || {}).code === 401) {
          return Observable.of(authenticationDuck.refreshTokenRequest(action))
        }

        try {
          const errors = [...(err.response.violations || []), ...(err.response.errors || [])].map((e) =>
            typeof e === 'string' ? e : e.message,
          )
          return Observable.of(fetchFailure(errors))
        } catch (e) {
          return Observable.of(fetchFailure([intl.formatMessage({ id: 'ducks.server' })]))
        }
      }),
  )
}

export function invitationRequestEpic(action$) {
  return action$.ofType(INVITATION_REQUEST).mergeMap((action) =>
    Observable.ajax(invite({ payload: action.payload }))
      .map((res) => invitationSuccess())
      .catch((err) => {
        if (typeof err === 'object' && (err.response || {}).code === 401) {
          return Observable.of(authenticationDuck.refreshTokenRequest(action))
        }

        try {
          const errors = [...(err.response.violations || []), ...(err.response.errors || [])].map((e) =>
            typeof e === 'string' ? e : e.message,
          )
          return Observable.of(invitationFailure(errors))
        } catch (e) {
          return Observable.of(invitationFailure([intl.formatMessage({ id: 'ducks.server' })]))
        }
      }),
  )
}

export function invitationSuccessEpic(action$, store) {
  return action$.ofType(INVITATION_SUCCESS).mergeMap((action) => {
    const profile = store.getState().profile.payload
    return Observable.of(
      ...[closeModal()].concat(profile.managedCompany ? [fetchRequest(profile.managedCompany, false)] : []),
    )
  })
}

export function disableRequestEpic(action$) {
  return action$.ofType(DISABLE_REQUEST).mergeMap((action) =>
    Observable.ajax(disable({ params: { id: action.courier.id } }))
      .map((res) => disableSuccess(action.courier))
      .catch((err) => {
        if (typeof err === 'object' && (err.response || {}).code === 401) {
          return Observable.of(authenticationDuck.refreshTokenRequest(action))
        }

        return Observable.of(disableFailure(action.courier))
      }),
  )
}

export function disableSuccessEpic(action$, store) {
  return action$.ofType(DISABLE_SUCCESS).mergeMap((action) => {
    const profile = store.getState().profile.payload
    return profile.managedCompany ? Observable.of(fetchRequest(profile.managedCompany, false)) : Observable.never()
  })
}

export function sendAlertOnDisableSuccessEpic(action$) {
  return action$
    .ofType(DISABLE_SUCCESS)
    .mapTo(alertSuccess(intl.formatMessage({ id: 'ducks.courier.disable.success' })))
}

export function sendAlertOnInvitationSuccessEpic(action$) {
  return action$
    .ofType(INVITATION_SUCCESS)
    .mapTo(alertSuccess(intl.formatMessage({ id: 'ducks.courier.invitation.success' })))
}

export function sendAlertOnfailureEpic(action$) {
  return action$.ofType(DISABLE_FAILURE, INVITATION_FAILURE, FETCH_FAILURE).mapTo(alertErrorDefault())
}
